import { ReactElement, useState, useCallback } from 'react';

import { useTheme } from '@mui/material';

import { debounce, merge } from 'lodash';

import useScrollStyle from './scroll';

import { useBoolean } from '../../../../common/hooks';
import Center from '../../../Center';
import { CenterProps as BodyProps } from '../../../Center/types';

export default function Body({ children, sx, ...rest }: BodyProps): ReactElement {
  const theme = useTheme();

  const scrollStyle = useScrollStyle(theme);

  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);

  const [hasOverflown, setHasOverflown] = useBoolean();

  const handleOverflow = useCallback(
    debounce((ref) => {
      if (ref) {
        const parent = ref.parentElement?.offsetHeight || 0;
        const header = ref.previousSibling?.offsetHeight || 0;
        const footer = ref.nextSibling?.offsetHeight || 0;

        const total = parent - header - footer;

        const height = ref.firstChild?.offsetHeight || 0;

        if (height >= total) {
          setHasOverflown.on();
        } else {
          setHasOverflown.off();
        }

        setHeaderHeight(header);
        setFooterHeight(footer);
      }
    }, 250),
    [theme]
  );

  return (
    <Center
      ref={handleOverflow}
      alignItems='flex-start'
      justifyContent='stretch'
      {...rest}
      sx={{
        ...merge(
          scrollStyle,
          {
            width: `calc(100% - ${theme.spacing(4)})`,
            maxHeight: `calc(100% - ${headerHeight + footerHeight}px)`,

            overflowY: 'auto',

            flex: 1,

            backgroundColor: 'transparent',

            pr: hasOverflown ? 2 : 0,
            m: 2
          },
          sx
        )
      }}>
      {children}
    </Center>
  );
}
